/**
 * Action Menu Component
 * Reusable dropdown menu component following SOLID principles
 *
 * - SRP: Single responsibility for dropdown menu rendering
 * - OCP: Open for extension via items prop
 * - ISP: Minimal, focused interface
 * - DIP: Depends on abstractions (menu items interface)
 */

import React from 'react';
import {
  spacing,
  typographyColors,
  typography,
  fontPrimary,
  borders,
  backgroundColors,
  strokeColors,
} from '@/design-system/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './dropdown-menu';
import { cn } from '@/lib/utils';

export interface ActionMenuItem {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  variant?: 'default' | 'destructive';
  icon?: React.ReactNode;
  iconSrc?: string;
}

export interface ActionMenuProps {
  /**
   * Menu items to display
   */
  items: ActionMenuItem[];
  /**
   * Trigger element (icon, button, etc.)
   */
  trigger: React.ReactNode;
  /**
   * Alignment of the dropdown menu
   */
  align?: 'start' | 'center' | 'end';
  /**
   * Additional styles for trigger button
   */
  triggerClassName?: string;
  triggerStyle?: React.CSSProperties;
  /**
   * Additional styles for dropdown content
   */
  contentClassName?: string;
  contentStyle?: React.CSSProperties;
  /**
   * Callback when trigger is clicked (for stopPropagation, etc.)
   */
  onTriggerClick?: (e: React.MouseEvent) => void;
  /**
   * Minimum width of the dropdown
   */
  minWidth?: string;
  /**
   * Side offset for dropdown positioning
   */
  sideOffset?: number;
}

/**
 * Default trigger button styles
 */
const defaultTriggerStyles: React.CSSProperties = {
  padding: spacing[2],
  border: 'none',
  background: backgroundColors.primary,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: borders.radius.sm,
};

/**
 * Default content styles
 */
const defaultContentStyles: React.CSSProperties = {
  backgroundColor: backgroundColors.primary,
  border: `${borders.width.thin} solid ${strokeColors.base}`,
  borderRadius: borders.radius.sm,
  padding: spacing[1],
  minWidth: '120px',
};

/**
 * Menu item icon component
 */
const MenuItemIcon: React.FC<{ src?: string; children?: React.ReactNode }> = ({
  src,
  children,
}) => {
  if (children) {
    return <>{children}</>;
  }
  if (src) {
    return (
      /* eslint-disable-next-line @next/next/no-img-element -- dynamic icon src */
      <img
        src={src}
        alt=''
        width={16}
        height={16}
        style={{
          display: 'block',
          objectFit: 'contain',
          flexShrink: 0,
        }}
        onError={e => {
          // Silently handle icon loading errors to prevent breaking the UI
          e.currentTarget.style.display = 'none';
        }}
      />
    );
  }
  return null;
};

/**
 * Reusable Action Menu Component
 *
 * @example
 * ```tsx
 * <ActionMenu
 *   items={[
 *     { label: 'Open', onClick: () => handleOpen() },
 *     { label: 'Delete', onClick: () => handleDelete(), variant: 'destructive' }
 *   ]}
 *   trigger={<EllipsisIcon />}
 *   align="end"
 * />
 * ```
 */
export const ActionMenu: React.FC<ActionMenuProps> = ({
  items,
  trigger,
  align = 'end',
  triggerClassName,
  triggerStyle,
  contentClassName,
  contentStyle,
  onTriggerClick,
  minWidth = '120px',
  sideOffset = 4,
}) => {
  const handleTriggerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onTriggerClick?.(e);
  };

  const handleItemSelect = (item: ActionMenuItem) => {
    if (!item.disabled) {
      item.onClick();
    }
  };

  if (items.length === 0) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        onClick={handleTriggerClick}
        style={{
          ...defaultTriggerStyles,
          ...triggerStyle,
        }}
        className={triggerClassName}
        aria-label='More options'
      >
        {trigger}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        sideOffset={sideOffset}
        style={{
          ...defaultContentStyles,
          minWidth,
          ...contentStyle,
        }}
        className={contentClassName}
      >
        {items.map((item, index) => (
          <DropdownMenuItem
            key={index}
            onSelect={() => handleItemSelect(item)}
            disabled={item.disabled}
            className={cn(
              item.variant === 'destructive'
                ? 'text-destructive focus:text-destructive'
                : '',
              'gap-2'
            )}
            style={{
              fontFamily: fontPrimary,
              fontSize: typography.size.sm,
              color:
                item.variant === 'destructive'
                  ? undefined
                  : typographyColors.primary,
            }}
          >
            <MenuItemIcon src={item.iconSrc}>{item.icon}</MenuItemIcon>
            <span>{item.label}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
